import React, { useState } from 'react'; // Import useState
import '../styles/Contact.css';
import SEO from './SEO';
import { getApiBase } from '../config/api'; // Import your API helper

const Contact = () => {
  // State for the form fields
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    honeypot: '' // This is our hidden bot-trap field
  });

  // State for loading and messages
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  // Handle input changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
    // Clear error when user starts typing
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: null }));
    }
  };

  // Validate the form
  const validateForm = () => {
    const newErrors = {};
    if (!form.name) newErrors.name = 'Name is required.';
    if (!form.email) {
      newErrors.email = 'Email is required.';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = 'Email address is invalid.';
    }
    if (!form.message) {
      newErrors.message = 'Message is required.';
    } else if (form.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters long.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Stop the page from reloading
    setMessage({ type: '', text: '' });

    // Bot check
    if (form.honeypot) {
      console.log("Bot detected!");
      return; // Silently fail
    }

    // Client-side validation
    if (!validateForm()) {
      setMessage({ type: 'error', text: 'Please fill out all required fields.' });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${getApiBase()}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (response.ok) {
        // Success!
        setMessage({ type: 'success', text: 'Thank you! Your message has been sent.' });
        setForm({ name: '', email: '', phone: '', message: '', honeypot: '' }); // Reset form
        setErrors({});
      } else {
        // Handle server errors (like "Message too short")
        setMessage({ type: 'error', text: data.message || 'An error occurred.' });
      }
    } catch (err) {
      // Handle network errors
      setMessage({ type: 'error', text: 'Failed to send message. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Contact Us - HS India" description="Contact HS India for inquiries, support, or business opportunities. We're here to help you." />
      <section className="Padding-Top">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="custom-heading">Get in Touch with HS India</h1>
              <hr className="custom-divider" />
            </div>
          </div>
          <div className="col-md-12">
            <div className="row">
              <div className="col-md-6 col-12">
                <div className='ContactDetails'>
                  <h5>Whether you're a retailer looking to stock our latest collection, a reseller ready to scale your business, or simply have a question — our team is here to help.</h5>
                  <p className="mb-1"><strong>Phone:</strong> <a href="tel:+919210740822" className="text-Primary">+91 9210740822</a></p>
                  <p className="mb-1"><strong>Email:</strong> <a href="mailto:info@hs-india.com" className="text-Primary">info@hs-india.com</a></p>
                  <p className="mb-1"><strong>Address:</strong><a href="https://maps.app.goo.gl/DxSXPhin1vcJ11cC6" className='text-Primary'> Gali Kishan Dutt, 1420-21, Nai Sarak, Maliwara, Roshanpura, Chandni Chowk, New Delhi, Delhi, 110006</a></p>
                  <div className="connected mt-3">Let’s Stay Connected:</div>
                  <div className="hs-social mt-2">
                    <a href="https://www.instagram.com/hsindiaa/?hl=en" aria-label="Instagram" className="me-2"><i className="bi bi-instagram"></i></a>
                    <a href='https://www.facebook.com/hsindiaofficial/' aria-label='Facebook'><i className='bi bi-facebook'></i></a>
                    <a href="https://www.linkedin.com/company/hoshiyar-singh-suresh-chandra-sarees-private-limited/" aria-label="LinkedIn"><i className="bi bi-linkedin"></i></a>
                  </div>
                </div>
              </div>
              <div className='col-md-6 col-12'>
                {/* Use the new handleSubmit function */}
                <form className="g-3" onSubmit={handleSubmit}>
                  
                  {/* Honeypot Field for Bot Protection */}
                  <div className="InputField" style={{ position: 'absolute', left: '-5000px', top: '-5000px' }} aria-hidden="true">
                    <label htmlFor="honeypot">Leave this empty</label>
                    <input
                      type="text"
                      id="honeypot"
                      value={form.honeypot}
                      onChange={handleChange}
                      autoComplete="off"
                      tabIndex="-1"
                    />
                  </div>

                  <div className="InputField">
                    <label htmlFor="name" className="form-label">Name</label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      id="name"
                      placeholder="Your Name"
                      value={form.name}
                      onChange={handleChange}
                    />
                    {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                  </div>
                  <div className="InputField">
                    <label htmlFor="email" className="form-label">Email</label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      id="email"
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={handleChange}
                    />
                    {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                  </div>
                  <div className="InputField">
                    <label htmlFor="phone" className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      id="phone"
                      placeholder="Your Phone"
                      value={form.phone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="InputField">
                    <label htmlFor="message" className="form-label">Message</label>
                    <textarea
                      className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                      id="message"
                      rows="4"
                      placeholder="How can we help you?"
                      value={form.message}
                      onChange={handleChange}
                    ></textarea>
                    {errors.message && <div className="invalid-feedback d-block">{errors.message}</div>}
                  </div>

                  {/* Message Display Area */}
                  {message.text && (
                    <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'} mt-3`}>
                      {message.text}
                    </div>
                  )}

                  <div className="InputField my-3">
                    <button type="submit" className="btn btn-primary Primary-Button px-4 py-2" disabled={loading}>
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-3 mb-0">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14947372.631334731!2d67.65320799999998!3d23.844867682911705!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfdff160f0ffd%3A0x6b4fef469ba9c048!2sHouse%20of%20Surya!5e0!3m2!1sen!2sin!4v1750771720584!5m2!1sen!2sin"
            width="100%"
            height="500"
            style={{ border: 0, }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="HS India Location Map"
          ></iframe>
        </div>
      </section>
    </>
  );
};

export default Contact;